import {
  NotificationsType,
  NotificationVariantType,
} from "./store/reducers/notificationsReducer";
import { NOTIFICATION_TIMEOUT, UNEXPECTED_ERROR_MESSAGE } from "./constants";
import { ReduxMessage } from "./store/reducers/messageListReducer";
import { ReduxConversation } from "./store/reducers/convoReducer";
import { getSdkConversationObject } from "./conversations-objects";

export const getTypingMessage = (typingData: string[]): string =>
  typingData.length > 1
    ? `${typingData.length + " participants are typing..."}`
    : `${typingData[0] + " is typing..."}`;

export const pushNotification = (
  messages: { variant: NotificationVariantType; message: string }[],
  func?: (messages: NotificationsType) => void
): void => {
  if (func) {
    func(
      messages.map(({ variant, message }) => ({
        variant,
        message,
        id: new Date().getTime(),
        dismissAfter: NOTIFICATION_TIMEOUT,
      }))
    );
  }
};

export const successNotification = ({
  message,
  addNotifications,
}: {
  message: string;
  addNotifications?: (messages: NotificationsType) => void;
}): void => {
  if (!addNotifications) {
    return;
  }
  pushNotification(
    [
      {
        message,
        variant: "success",
      },
    ],
    addNotifications
  );
};

export const unexpectedErrorNotification = (
  addNotifications?: (messages: NotificationsType) => void
): void => {
  if (!addNotifications) {
    return;
  }
  pushNotification(
    [
      {
        message: UNEXPECTED_ERROR_MESSAGE,
        variant: "error",
      },
    ],
    addNotifications
  );
};

export const handlePromiseRejection = async (
  func: () => void,
  addNotifications?: (messages: NotificationsType) => void
): Promise<void> => {
  if (!addNotifications) {
    return;
  }
  try {
    await func();
  } catch (e) {
    // unexpectedErrorNotification(addNotifications);
    // return Promise.reject(UNEXPECTED_ERROR_MESSAGE);
    console.error("Unexpected error", e);
  }
};

// Shared message utilities
type BotMessageItem = {
  template_sid?: string;
  user_reply_body?: string;
  message_outbound?: string;
  timestamp?: string;
};

const normalizeText = (v?: string | null) =>
  (v ?? "").toString().trim().toLowerCase();

const safeParseJson = (value: unknown): unknown => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value ?? null;
};

const isBotMessageItem = (v: unknown): v is BotMessageItem => {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    (o.message_outbound === undefined ||
      typeof o.message_outbound === "string") &&
    (o.user_reply_body === undefined ||
      typeof o.user_reply_body === "string") &&
    (o.template_sid === undefined || typeof o.template_sid === "string") &&
    (o.timestamp === undefined || typeof o.timestamp === "string")
  );
};

const extractBotMessages = (attrs: unknown): BotMessageItem[] => {
  const parsed = safeParseJson(attrs);
  if (parsed && typeof parsed === "object") {
    const maybe = (parsed as Record<string, unknown>)["botMessages"];
    if (Array.isArray(maybe) && maybe.every(isBotMessageItem))
      return maybe as BotMessageItem[];
  }
  return [];
};

// Cache mapped bot messages per attributes identity to avoid repeated mapping
const botMapCacheByObj = new WeakMap<object, ReduxMessage[]>();
const botMapCacheByStr = new Map<string, ReduxMessage[]>();

function getMappedBotsFromAttributes(
  attrs: unknown,
  username: string
): ReduxMessage[] {
  // attributes can be string or object
  if (typeof attrs === "string") {
    const cached = botMapCacheByStr.get(attrs);
    if (cached) return cached;
    const bots = extractBotMessages(attrs);
    const mapped = bots
      .map((item) => ({
        outboundText: (item?.message_outbound ?? "").toString().trim(),
        replyText: (item?.user_reply_body ?? "").toString(),
        timestamp: new Date(item?.timestamp ?? Date.now()),
      }))
      .filter((m) => !!m.outboundText)
      .map((meta, idx) => ({
        sid: `bot-${meta.timestamp.getTime()}-${idx}`,
        index: -2,
        body: meta.outboundText,
        author: username,
        attributes: {},
        participantSid: null,
        dateCreated: meta.timestamp,
        attachedMedia: null,
        aggregatedDeliveryReceipt: null,
        // store reply text in attributes for fast match step
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        __replyText: meta.replyText,
      })) as ReduxMessage[];
    // ensure ascending order
    mapped.sort(
      (a, b) =>
        new Date(a.dateCreated ?? 0).getTime() -
        new Date(b.dateCreated ?? 0).getTime()
    );
    botMapCacheByStr.set(attrs, mapped);
    return mapped;
  }
  if (attrs && typeof attrs === "object") {
    const cached = botMapCacheByObj.get(attrs as object);
    if (cached) return cached;
    const bots = extractBotMessages(attrs);
    const mapped = bots
      .map((item) => ({
        outboundText: (item?.message_outbound ?? "").toString().trim(),
        replyText: (item?.user_reply_body ?? "").toString(),
        timestamp: new Date(item?.timestamp ?? Date.now()),
      }))
      .filter((m) => !!m.outboundText)
      .map((meta, idx) => ({
        sid: `bot-${meta.timestamp.getTime()}-${idx}`,
        index: -2,
        body: meta.outboundText,
        author: username,
        attributes: {},
        participantSid: null,
        dateCreated: meta.timestamp,
        attachedMedia: null,
        aggregatedDeliveryReceipt: null,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        __replyText: meta.replyText,
      })) as ReduxMessage[];
    mapped.sort(
      (a, b) =>
        new Date(a.dateCreated ?? 0).getTime() -
        new Date(b.dateCreated ?? 0).getTime()
    );
    botMapCacheByObj.set(attrs as object, mapped);
    return mapped;
  }
  return [];
}

export function buildCombinedMessages(
  messages: ReduxMessage[] = [],
  convo: ReduxConversation
): ReduxMessage[] {
  let attrs: unknown = null;
  try {
    const sdkConvo = getSdkConversationObject(convo);
    attrs = sdkConvo?.attributes as unknown;
  } catch {
    attrs = null;
  }
  const username = localStorage.getItem("username") ?? "";
  const baseMappedBots = getMappedBotsFromAttributes(attrs, username);
  // clone to avoid mutating cache when adjusting timestamps
  const mappedBotMessages: ReduxMessage[] = baseMappedBots.map((m) => ({
    ...m,
  }));

  const fiveSecondsMs = 5_000;
  // two-pointer adjustment within a 5s window using replyText stored in __replyText
  let j = 0;
  const getMsgTime = (m?: ReduxMessage) =>
    m?.dateCreated ? new Date(m.dateCreated).getTime() : 0;
  for (let i = 0; i < mappedBotMessages.length; i++) {
    const botMsg = mappedBotMessages[i] as ReduxMessage & {
      __replyText?: string;
    };
    const botTs = getMsgTime(botMsg);
    if (!botTs) continue;
    const windowStart = botTs - fiveSecondsMs;
    const windowEnd = botTs + fiveSecondsMs;
    while (j < messages.length && getMsgTime(messages[j]) < windowStart) j++;
    let k = j;
    const replyNorm = normalizeText(botMsg.__replyText ?? "");
    if (!replyNorm) continue;
    while (k < messages.length) {
      const mTs = getMsgTime(messages[k]);
      if (mTs > windowEnd) break;
      if (normalizeText(messages[k].body) === replyNorm) {
        if (mTs <= botTs)
          (botMsg as ReduxMessage).dateCreated = new Date(mTs - 1);
        break;
      }
      k++;
    }
  }

  // merge two ascending arrays instead of re-sorting all items
  const combined: ReduxMessage[] = [];
  let iMsg = 0;
  let iBot = 0;
  const cmp = (a: ReduxMessage, b: ReduxMessage) => {
    const at = new Date(a.dateCreated ?? 0).getTime();
    const bt = new Date(b.dateCreated ?? 0).getTime();
    if (at !== bt) return at - bt;
    if (a.index !== b.index) return a.index - b.index;
    return String(a.sid).localeCompare(String(b.sid));
  };
  while (iMsg < messages.length && iBot < mappedBotMessages.length) {
    if (cmp(messages[iMsg], mappedBotMessages[iBot]) <= 0) {
      combined.push(messages[iMsg++]);
    } else {
      combined.push(mappedBotMessages[iBot++]);
    }
  }
  while (iMsg < messages.length) combined.push(messages[iMsg++]);
  while (iBot < mappedBotMessages.length)
    combined.push(mappedBotMessages[iBot++]);

  return combined;
}

export function getLastCombinedMessageText(
  messages: ReduxMessage[] = [],
  convo: ReduxConversation
): string {
  const combined = buildCombinedMessages(messages, convo);
  if (!combined.length) return "Sem mensagens";
  return combined[combined.length - 1]?.body || "Media message";
}

// Optimized: compute only the last message text without building/sorting all items
export function getLastCombinedMessageTextFast(
  messages: ReduxMessage[] = [],
  convo: ReduxConversation
): string {
  // try to read attributes; if not available, treat as no bot messages
  let attrs: unknown = null;
  try {
    const sdkConvo = getSdkConversationObject(convo);
    attrs = sdkConvo?.attributes as unknown;
  } catch {
    attrs = null;
  }

  const bots = extractBotMessages(attrs);
  const lastBot = bots.length ? bots[bots.length - 1] : undefined;
  let latestBotTs = lastBot ? new Date(lastBot?.timestamp ?? 0).getTime() : 0;
  const latestBotText = (lastBot?.message_outbound ?? "").toString();

  const lastNormal = messages.length
    ? messages[messages.length - 1]
    : undefined;
  const normalTs = lastNormal?.dateCreated
    ? new Date(lastNormal.dateCreated).getTime()
    : 0;

  // Ajusta caso a resposta do usuário esteja muito próxima do envio do bot
  if (lastBot && lastNormal?.dateCreated) {
    const replyText = (lastBot.user_reply_body ?? "").toString();
    const replyTs = new Date(lastNormal.dateCreated).getTime();
    const fiveSecondsMs = 5_000;
    if (
      Math.abs(replyTs - (latestBotTs || 0)) <= fiveSecondsMs &&
      normalizeText(lastNormal.body) === normalizeText(replyText)
    ) {
      if (replyTs <= (latestBotTs || 0)) latestBotTs = replyTs - 1;
    }
  }

  if (!lastNormal && !latestBotTs) return "Sem mensagens";
  if (!lastNormal && latestBotTs) return latestBotText || "Media message";
  if (latestBotTs && latestBotTs >= normalTs)
    return latestBotText || "Media message";

  return lastNormal?.body || "Media message";
}
